import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Play, Square, Flag, Pencil, AlertTriangle, Link2, Lock } from 'lucide-react';
import { TIMING, STATE_LABEL, fmtTime } from './taUtils';

function Who({ a }) {
    const p = a.end_by || a.start_by;
    if (!p) return <span className="text-muted-foreground">—</span>;
    return (
        <span className="text-xs">
            {p.name}
            {a.source && a.source !== 'manual' && <span className="block text-[10px] text-muted-foreground">{({ checklist: 'vía checklist', despacho: 'vía despacho', bitacora: 'vía bitácora', migrado_checklist: 'migrado del checklist' })[a.source] || a.source}</span>}
        </span>
    );
}

function Actions({ a, busy, onAction, onCorrect, onDelay }) {
    if (a.derived) return <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Link2 className="w-3 h-3" />{a.link_label}</span>;
    if (!a.can_record) return <span className="flex items-center gap-1 text-[10px] text-muted-foreground" data-testid={`ta-readonly-${a.key}`}><Lock className="w-3 h-3" />Solo lectura</span>;
    const isMs = !a.duration_min;
    return (
        <div className="flex flex-wrap items-center gap-1 justify-end">
            {isMs && !a.actual_end && (
                <Button size="sm" className="h-7 px-2 text-xs" disabled={busy} onClick={() => onAction(a, 'milestone')} data-testid={`ta-milestone-${a.key}`}><Flag className="w-3 h-3 mr-1" />Registrar</Button>
            )}
            {!isMs && !a.actual_start && (
                <Button size="sm" className="h-7 px-2 text-xs" disabled={busy} onClick={() => onAction(a, 'start')} data-testid={`ta-start-${a.key}`}><Play className="w-3 h-3 mr-1" />Registrar inicio</Button>
            )}
            {!isMs && a.actual_start && !a.actual_end && (
                <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" disabled={busy} onClick={() => onAction(a, 'end')} data-testid={`ta-end-${a.key}`}><Square className="w-3 h-3 mr-1" />Registrar fin</Button>
            )}
            {(a.actual_start || a.actual_end) && (
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onCorrect(a)} title="Corregir hora" data-testid={`ta-correct-${a.key}`}><Pencil className="w-3.5 h-3.5" /></Button>
            )}
            {a.timing === 'excedido' && !a.delay_cause && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-red-500/40 text-red-700 dark:text-red-300" onClick={() => onDelay(a)} data-testid={`ta-delay-${a.key}`}><AlertTriangle className="w-3 h-3 mr-1" />Causa</Button>
            )}
        </div>
    );
}

export function ActivityTable({ view, busy, onAction, onCorrect, onDelay, showPre }) {
    const tz = view.timezone;
    const rows = view.activities.filter(a => showPre || a.offset_min >= 0);
    return (
        <div className="overflow-x-auto rounded-lg border border-border/40" data-testid="ta-activity-table">
            <table className="w-full text-sm">
                <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                        {['Actividad', 'Área', 'Planificada', 'Inicio real', 'Fin real', 'Estado', 'Registró', 'Acción'].map(h => <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(a => {
                        const t = TIMING[a.timing] || TIMING.sin_iniciar;
                        return (
                            <tr key={a.key} className="border-t border-border/20 hover:bg-muted/10" data-testid={`ta-row-${a.key}`}>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-1.5">
                                        {a.critical && <span className="w-1.5 h-1.5 rounded-full bg-red-500" title="Ruta crítica" />}
                                        <span className="font-medium">{a.name}</span>
                                        {!a.duration_min && <span className="text-[9px] text-muted-foreground border border-border/40 rounded px-1">hito</span>}
                                    </div>
                                    {a.drag_min > 0 && <span className="text-[10px] text-amber-700 dark:text-amber-400" data-testid={`ta-drag-${a.key}`}>Arrastra +{a.drag_min} min</span>}
                                    {a.delay_cause && <span className="block text-[10px] text-red-700 dark:text-red-300" data-testid={`ta-cause-${a.key}`}>Causa: {a.delay_cause.cause}{a.delay_cause.note ? ` — ${a.delay_cause.note}` : ''}</span>}
                                </td>
                                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{a.area}{a.crew_position ? ` · ${a.crew_position}` : ''}</td>
                                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{fmtTime(a.planned_start, tz)}{a.duration_min ? `–${fmtTime(a.planned_end, tz)}` : ''}</td>
                                <td className="px-3 py-2 font-mono text-xs" data-testid={`ta-actual-start-${a.key}`}>{fmtTime(a.actual_start, tz)}</td>
                                <td className="px-3 py-2 font-mono text-xs" data-testid={`ta-actual-end-${a.key}`}>{fmtTime(a.actual_end, tz)}</td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                    <Badge variant="outline" className={`text-[10px] ${t.badge}`} data-testid={`ta-timing-${a.key}`}>{a.state === 'pendiente' ? STATE_LABEL.pendiente : t.label}</Badge>
                                    {a.state === 'en_curso' && <span className="block text-[10px] text-sky-700 dark:text-sky-300 mt-0.5">En curso</span>}
                                </td>
                                <td className="px-3 py-2"><Who a={a} /></td>
                                <td className="px-3 py-2 text-right"><Actions a={a} busy={busy} onAction={onAction} onCorrect={onCorrect} onDelay={onDelay} /></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
